import { useEffect, useState } from "react";
import { DeviceMobile, SignOut, Trash, ShieldCheck, PencilSimple, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function parseUA(ua) {
  ua = ua || "";
  const browser = /Edg/.test(ua) ? "Edge" : /OPR|Opera/.test(ua) ? "Opera" : /Chrome/.test(ua) ? "Chrome" : /Firefox/.test(ua) ? "Firefox" : /Safari/.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS X|Macintosh/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad|iOS/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "Unknown OS";
  return `${browser} · ${os}`;
}
const fmtDate = (s) => { try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s || "—"; } };

export default function Security() {
  const [devices, setDevices] = useState(null);
  const [editFp, setEditFp] = useState(null);
  const [draft, setDraft] = useState("");
  const load = () => api.get("/auth/devices").then((r) => setDevices(r.data)).catch(() => setDevices([]));
  useEffect(() => { load(); }, []);

  const startRename = (d) => { setEditFp(d.fp); setDraft(d.label || ""); };
  const saveRename = async (fp) => {
    try { await api.patch(`/auth/devices/${fp}`, { label: draft }); setEditFp(null); load(); toast.success("Device renamed"); }
    catch { toast.error("Could not rename device"); }
  };

  const revoke = async (fp) => {
    try { await api.delete(`/auth/devices/${fp}`); toast.success("Device forgotten — it will re-alert on next sign-in"); load(); }
    catch { toast.error("Could not remove device"); }
  };
  const signoutAll = async () => {
    try {
      await api.post("/auth/devices/signout-all");
      toast.success("Signed out of all sessions");
      await api.post("/auth/logout").catch(() => {});
      setTimeout(() => { window.location.href = "/login"; }, 800);
    } catch { toast.error("Could not sign out everywhere"); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="security-page">
      <PageHeader overline="My Account" title="Sign-in Devices" subtitle="Devices that have signed in to your account. You get an email when a new one appears." />

      <Card className="p-6 border-border space-y-4">
        <SectionTitle icon={DeviceMobile}>Recognised devices</SectionTitle>
        {devices === null && <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading devices…</div>}
        {devices && devices.length === 0 && <p className="text-sm text-muted-foreground">No devices recorded yet.</p>}
        <div className="space-y-2" data-testid="device-list">
          {devices && devices.map((d) => (
            <div key={d.fp} className="flex items-center justify-between gap-3 p-3 rounded-md bg-muted border border-border" data-testid={`device-${d.fp}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-primary flex items-center gap-2">
                  {editFp === d.fp ? (
                    <span className="flex items-center gap-1">
                      <input className="border rounded px-1.5 py-0.5 text-xs" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={parseUA(d.ua)} data-testid={`device-label-input-${d.fp}`} autoFocus />
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-600" onClick={() => saveRename(d.fp)} data-testid={`device-save-${d.fp}`}><Check size={14} /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditFp(null)}><X size={14} /></Button>
                    </span>
                  ) : (
                    <>
                      {d.label || parseUA(d.ua)}
                      <button className="text-muted-foreground hover:text-primary" onClick={() => startRename(d)} data-testid={`device-rename-${d.fp}`}><PencilSimple size={13} /></button>
                      {d.is_current && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`device-current-${d.fp}`}>This device</span>}
                    </>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">{d.label ? `${parseUA(d.ua)} · ` : ""}IP {d.ip || "unknown"} · first seen {fmtDate(d.first_seen)}</div>
              </div>
              {d.is_current ? (
                <span className="text-xs text-muted-foreground shrink-0">Active now</span>
              ) : (
                <Button size="sm" variant="outline" className="rounded-full text-xs text-destructive shrink-0" onClick={() => revoke(d.fp)} data-testid={`revoke-device-${d.fp}`}>
                  <Trash size={14} className="mr-1" /> Forget
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-border space-y-3">
        <SectionTitle icon={ShieldCheck}>Account security</SectionTitle>
        <p className="text-sm text-muted-foreground">If you suspect someone else has access, sign out of every device at once. You will need to sign in again.</p>
        <Button variant="outline" className="rounded-full text-destructive w-fit" onClick={signoutAll} data-testid="signout-all-btn">
          <SignOut size={16} className="mr-1" /> Sign out of all sessions
        </Button>
      </Card>
    </div>
  );
}
